import { groupBy, isEmpty } from 'lodash-es';
import moment from 'moment';
import React from 'react';
import { Bar, CartesianGrid, ComposedChart, Line, Tooltip, XAxis, YAxis } from 'recharts';

import { IDashboardHomeCardMetricKey } from '~/src/dash/api/dashboards/home';
import { IDashboardCard } from '~/src/dash/components/cards/common';
import {
  formatTooltipDate,
  formatTooltipValue,
  IDashboardChartMetric,
  YScale,
} from '~/src/dash/components/charts/common';
import { LoadingChart } from '~/src/dash/components/charts/LoadingChart';
import { Col, Cols, fontSizing, Row, Rows, spacing, Text, usePanel } from '~/src/dash/publicaUI';
import { convertLargeNumbersToRoundedString } from '~/src/dash/utils/basic';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState, LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { calculateTrend, displayArrow, getTrendColors } from '~/src/dash/utils/trends';
import { useBaseStore } from '~/src/dash/views/base/contexts';

const chartColors = [
  ['#6F60DD', '#A89CFF'],
  ['#FF4E29', '#F5AE9F'],
  ['#F3C628', '#FFE380'],
  ['#00B7A9', '#A3EAE4'],
  ['#FB6376', '#FFC3CB'],
  ['#FFA737', '#FFDAA9'],
  ['#40916C', '#78DDAF'],
  ['#FF9CEF', '#FFC6F6'],
  ['#4EA3F1', '#93C6F5'],
  ['#A34FC2', '#DAA5EB'],
];

const renderItems = (
  publishers: string[],
  metrics: IDashboardChartMetric[],
  numItems: number,
  isCompareEnabled?: boolean,
) => {
  const items: any[] = [];

  metrics.forEach((metric) => {
    const yAxisId = metric.yScale || 'base';
    if (metric.chartType === 'bar') {
      for (let i = 0; i < publishers.length; i++) {
        items.push(
          <Bar
            name={metric.label}
            dataKey={`main.${publishers[i]}.${metric.key}`}
            barSize={16}
            fill={chartColors[i % chartColors.length][0]}
            yAxisId={yAxisId}
            stackId='main'
          />,
        );
        if (isCompareEnabled) {
          items.push(
            <Bar
              name={metric.label}
              dataKey={`compare.${publishers[i]}.${metric.key}`}
              barSize={16}
              fill={chartColors[i % chartColors.length][1]}
              yAxisId={yAxisId}
              stackId='compare'
            />,
          );
        }
      }
    } else if (metric.chartType === 'line') {
      for (let i = 0; i < publishers.length; i++) {
        items.push(
          <Line
            name={metric.label}
            type='monotone'
            dataKey={`main.${publishers[i]}.${metric.key}`}
            stroke={chartColors[i % chartColors.length][0]}
            fill={chartColors[i % chartColors.length][0]}
            strokeWidth={2}
            dot={numItems <= 15}
            yAxisId={yAxisId}
          />,
        );
        if (isCompareEnabled) {
          items.push(
            <Line
              name={metric.label}
              type='monotone'
              dataKey={`compare.${publishers[i]}.${metric.key}`}
              stroke={chartColors[i % chartColors.length][1]}
              fill={chartColors[i % chartColors.length][1]}
              strokeWidth={2}
              dot={false}
              yAxisId={yAxisId}
            />,
          );
        }
      }
    }
  });
  return items;
};

interface ITrendDisplayProps {
  value: number | null;
  isInverseTrend?: boolean;
}

const TrendDisplay = ({ value, isInverseTrend }: ITrendDisplayProps) => {
  if (!value) {
    return <Text extraSmall bold color={publicaColors.gray} disableSelect></Text>;
  }
  const colors = getTrendColors(value, isInverseTrend);
  return (
    <Cols
      gap='4px'
      style={{
        justifyContent: 'space-around',
        alignItems: 'center',
        backgroundColor: colors.background,
        borderRadius: '4px',
        padding: '1px 6px 0px 4px',
      }}
    >
      <Col displayFlex width={16}>
        {displayArrow(value, colors.text)}
      </Col>
      <Col width={52}>
        <Text extraSmall bold color={colors.text} disableSelect>
          {`${Math.abs(value).toFixed(2)}%`}
        </Text>
      </Col>
    </Cols>
  );
};

const tooltipColWidths = {
  labels: 128,
  values: 112,
  trends: 72,
};

interface ICustomTooltipProps {
  active: boolean;
  payload: any;
  label: string;
  metricsConfig: Record<IDashboardHomeCardMetricKey, IDashboardCard>;
  grouping: number;
  currencySymbol: string;
  isCompareEnabled?: boolean;
  numStacks: number;
}

const CustomTooltip = ({
  active,
  payload,
  metricsConfig,
  grouping,
  currencySymbol,
  isCompareEnabled,
  numStacks,
}: ICustomTooltipProps) => {
  if (active && !isEmpty(payload)) {
    const groupedPayloads = groupBy(payload, 'name');
    const isMissingDataSet = numStacks * 2 !== payload.length;
    const isInitialSetMain = payload[0].dataKey.slice(0, 4) === 'main';

    return (
      <div
        style={{
          backgroundColor: publicaColors.white,
          border: `1px solid ${publicaColors.lightGray}`,
          padding: `${spacing.small} ${spacing.base}`,
        }}
      >
        {Object.entries(groupedPayloads).map((array: any) => {
          const [label, payload] = array;
          const filteredPayload =
            isCompareEnabled && !isMissingDataSet
              ? payload.filter((_l: any, i: number) => i % 2 === 0).slice(0, 9)
              : payload;
          const templateKeys = payload[0].dataKey.split('.');
          const hasCompareData = isCompareEnabled && !isEmpty(payload[0].payload.compare);
          return (
            <Rows paddingBottom={spacing.small}>
              <Row
                noPadding
                marginBottom={spacing.small}
                style={{
                  borderBottom: `1px solid ${publicaColors.lightGray}`,
                }}
              >
                <Cols
                  gap={spacing.large}
                  marginBottom={spacing.extraSmall}
                  style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Col width={tooltipColWidths.labels}>
                    <Text small bold>
                      {label}
                    </Text>
                  </Col>
                  {isInitialSetMain && (
                    <Col width={tooltipColWidths.values} style={{ textAlign: 'right' }}>
                      <Text small bold>
                        {formatTooltipDate(payload[0].payload.Date, grouping)}
                      </Text>
                    </Col>
                  )}
                  {hasCompareData && (
                    <>
                      <Col width={tooltipColWidths.values} style={{ textAlign: 'right' }}>
                        <Text small bold>
                          {formatTooltipDate(
                            payload[0].payload.compare[templateKeys[1]].Date,
                            grouping,
                          )}
                        </Text>
                      </Col>
                      <Col width={tooltipColWidths.trends} style={{ textAlign: 'right' }}>
                        <Text small bold>
                          Trends
                        </Text>
                      </Col>
                    </>
                  )}
                </Cols>
              </Row>
              {filteredPayload.map((item: any) => {
                const [range, publisher, metricKey] = item.dataKey.split('.');
                const { valueType, isInverseTrend } =
                  metricsConfig[metricKey as IDashboardHomeCardMetricKey];
                const mainValue = item.payload[range][publisher][metricKey];
                const compareValue = hasCompareData
                  ? item.payload.compare[publisher][metricKey]
                  : 0;
                return (
                  <Row noPadding>
                    <Cols
                      gap={spacing.extraLarge}
                      style={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Col width={tooltipColWidths.labels}>
                        <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
                          <Col paddingBottom='1px'>
                            <Text color={item.fill} style={{ fontSize: fontSizing.mini }}>
                              ⬤
                            </Text>
                          </Col>
                          <Col flex>
                            <Text small bold>
                              {publisher}
                            </Text>
                          </Col>
                        </Cols>
                      </Col>
                      {isInitialSetMain && (
                        <Col width={tooltipColWidths.values} style={{ textAlign: 'right' }}>
                          <Text small bold>
                            {formatTooltipValue(mainValue, valueType, currencySymbol)}
                          </Text>
                        </Col>
                      )}
                      {hasCompareData && (
                        <>
                          <Col width={tooltipColWidths.values} style={{ textAlign: 'right' }}>
                            <Text small bold>
                              {formatTooltipValue(compareValue, valueType, currencySymbol)}
                            </Text>
                          </Col>
                          <Col width={tooltipColWidths.trends} style={{ textAlign: 'right' }}>
                            <TrendDisplay
                              value={calculateTrend(mainValue, compareValue)}
                              isInverseTrend={!!isInverseTrend}
                            />
                          </Col>
                        </>
                      )}
                    </Cols>
                  </Row>
                );
              })}
            </Rows>
          );
        })}
      </div>
    );
  }
  return null;
};

interface IScale {
  hide: boolean;
  orientation: 'left' | 'right';
}

// helps determine which yAxes are visible
const getScalesInfo = (metrics: IDashboardChartMetric[]) => {
  const visibleScales: YScale[] = [];
  let isBaseVisible = false;
  for (const metric of metrics) {
    const yScale = metric.yScale || 'base';
    // if base scale is active, it must default to visible on the left
    if (yScale === 'base') isBaseVisible = true;
    if (!visibleScales.includes(yScale)) {
      if (yScale === 'base') {
        visibleScales.unshift(yScale);
      } else {
        visibleScales.push(yScale);
      }
    }
  }
  const scales: Record<string, IScale> = {
    base: { hide: true, orientation: 'left' },
    small: { hide: true, orientation: 'left' },
    large: { hide: true, orientation: 'left' },
  };
  // we only show 2 axes at a time
  const limit = Math.min(2, visibleScales.length);
  let numVisibleAxes = 0;
  for (let i = 0; i < limit; i++) {
    const scale = visibleScales[i];
    scales[scale].hide = false;
    if (scale !== 'base') {
      scales[scale].orientation = !isBaseVisible && i === 0 ? 'left' : 'right';
    }
    numVisibleAxes++;
  }
  return { scales, numVisibleAxes };
};

interface IStackedCompareChartProps {
  isCompareEnabled?: boolean;
  loadState: LoadingState;
  metrics: IDashboardChartMetric[];
  data: Array<Record<string, any>>;
  metricsConfig: Record<IDashboardHomeCardMetricKey, IDashboardCard>;
  grouping: number;
  publishers: string[];
}

export const StackedCompareChart = ({
  isCompareEnabled,
  data = [],
  metrics,
  metricsConfig,
  grouping,
  loadState,
  publishers,
}: IStackedCompareChartProps) => {
  const { width } = usePanel();
  const { currencySymbol } = useBaseStore();

  const { scales, numVisibleAxes } = getScalesInfo(metrics);

  const formatTimestamp = (date: string | moment.Moment): string => {
    if (!date) {
      return '';
    }
    return moment(date)
      .utc()
      .format(grouping ? 'M/D' : 'HH:mm');
  };

  return (
    <div
      style={{
        width: '100%',
        height: '280px',
      }}
    >
      {loadState === LoadingStateLoading ? (
        <LoadingChart />
      ) : (
        <ComposedChart
          width={(width || 0) - 80}
          height={280}
          data={data}
          barGap={4}
          margin={{
            top: 0,
            right: 0,
            left: 0,
            bottom: 0,
          }}
        >
          <Tooltip
            content={
              // this bypasses an issue with automated props from recharts
              // @ts-ignore
              <CustomTooltip
                metricsConfig={metricsConfig}
                grouping={grouping}
                currencySymbol={currencySymbol}
                isCompareEnabled={isCompareEnabled}
                numStacks={publishers.length}
              />
            }
            cursor={{ fill: publicaColors.lightGray }}
          />
          <CartesianGrid strokeDasharray='3 3' vertical={false} />
          {renderItems(publishers, metrics, data.length, isCompareEnabled)}
          <XAxis
            dataKey='Date'
            tick={{
              fontSize: 12,
              fill: 'black',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
            // @ts-ignore
            tickFormatter={formatTimestamp}
            domain={[0, data.length]}
          />
          <YAxis
            type='number'
            domain={[0, 'auto']}
            tick={{ fontSize: 12, fill: 'black', textAnchor: 'end' }}
            tickFormatter={convertLargeNumbersToRoundedString}
            yAxisId='base'
            hide={scales.base.hide}
            orientation='left'
          />
          <YAxis
            type='number'
            domain={[0, 'auto']}
            tick={{
              fontSize: 12,
              fill: 'black',
              textAnchor: scales.large.orientation === 'left' ? 'end' : 'start',
            }}
            tickFormatter={convertLargeNumbersToRoundedString}
            yAxisId='large'
            hide={scales.large.hide}
            orientation={scales.large.orientation}
          />
          <YAxis
            type='number'
            domain={[0, 'auto']}
            tick={{
              fontSize: 12,
              fill: 'black',
              textAnchor: scales.small.orientation === 'left' ? 'end' : 'start',
            }}
            tickFormatter={convertLargeNumbersToRoundedString}
            yAxisId='small'
            hide={scales.small.hide}
            orientation={scales.small.orientation}
          />
          <YAxis
            type='number'
            domain={[0, 'auto']}
            yAxisId='placeholder-left'
            hide={metrics.length !== 0}
            orientation='left'
          />
          <YAxis
            type='number'
            domain={[0, 'auto']}
            yAxisId='placeholder-right'
            hide={numVisibleAxes > 1}
            orientation='right'
          />
        </ComposedChart>
      )}
    </div>
  );
};
